import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useNavigate } from "react-router-dom";
import BuildPageStore from "~/stores/BuildPageStore";
import Job, { type Props as JobComponentProps } from "~/components/job/Job";
import { useCallback, useEffect } from "react";
import { CommandJob, Job as JobType } from "~/components/build/Show/lib/types";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import { useQueryClient } from "@tanstack/react-query";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";
import { setJobQueryData } from "~/components/build/Show/lib/queries/job";
import { testFailuresQueryOptions } from "~/components/build/Show/lib/queries/testFailures";
import { shouldFetchTestFailureMarkers } from "~/components/job/Job/Log/useTestFailureMarkers";
import { useJobTabSync } from "~/components/build/Show/components/StepDrawer/useJobTabSync";
import { useStepDrawer } from "~/components/build/Show/components/StepDrawer/StepDrawerContext";

export const CommandJobDetails = () => {
  const { job } = useStepDrawer();
  const { build, setBuildData } = useBuild();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { activeTab, onTabChange } = useJobTabSync({ job });

  useEffect(() => {
    if (job) {
      BuildPageStore.expandJob(job);
    }
  }, [job?.id]);

  // Prefetch test failure markers as soon as the job drawer opens
  const commandJob = job && job.type === "script" ? (job as CommandJob) : null;
  useEffect(() => {
    if (commandJob && shouldFetchTestFailureMarkers(commandJob)) {
      queryClient.prefetchQuery(testFailuresQueryOptions(build, commandJob.id));
    }
  }, [commandJob?.id, build]);

  const handleRedirectToLatestJob = useCallback(
    (latestJobUUID: string, latestJob?: JobType) => {
      if (latestJob) {
        setJobQueryData(build.id, latestJob, queryClient);
      }
      navigate(resolveJobUrl(latestJobUUID));
    },
    [build, queryClient, navigate],
  );

  if (!job) {
    return null;
  }

  return (
    <LogLineSelectionProvider>
      <div className="job-list-pipeline flex h-full flex-col">
        <Job
          key={job.id}
          job={job as JobComponentProps["job"]}
          build={build}
          setBuildData={setBuildData}
          autoFollow={true}
          onRetrySuccess={handleRedirectToLatestJob}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </div>
    </LogLineSelectionProvider>
  );
};
